/**
 * Where a "open this ticket" deep link (custody notification, FOH queue
 * accept, Birdseye, OptiBoard …) must take the Dayboard.
 *
 * The Dayboard renders a card only when `scheduled_date` equals the selected
 * day, the ticket is not deleted and is not an estimate (Dashboard's render
 * filter). The link carries only a ticket id, so a ticket scheduled for
 * another day used to land on whatever date the advisor was viewing — no
 * highlight, no Command Center, no message.
 *
 * `scheduled_date` is already the SHOP-LOCAL day (the start_time trigger
 * writes it in the shop's zone), so it is used as-is — never re-derived from a
 * UTC instant in the browser's zone.
 */
import { isTicketDeleted } from '@/utils/ticketDeletedState';

export interface DeepLinkTicketFacts {
  id: string;
  scheduled_date?: string | null;
  is_estimate?: boolean | null;
  deleted_at?: string | null;
  status?: string | null;
}

export type TicketDeepLinkPlan =
  | { kind: 'stay' }                                   // already on its day
  | { kind: 'select_day'; day: string }                // switch the Dayboard first
  | { kind: 'fallback'; reason: 'not_found' | 'no_board_date' | 'not_on_board' };

export const DEEP_LINK_TICKET_COLUMNS = 'id, scheduled_date, is_estimate, deleted_at, status, simple_ticket_number';

export function planTicketDeepLink(
  ticket: DeepLinkTicketFacts | null | undefined,
  selectedDay: string,
): TicketDeepLinkPlan {
  if (!ticket) return { kind: 'fallback', reason: 'not_found' };
  if (isTicketDeleted(ticket) || ticket.is_estimate === true) return { kind: 'fallback', reason: 'not_on_board' };
  const day = typeof ticket.scheduled_date === 'string' ? ticket.scheduled_date.slice(0, 10) : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return { kind: 'fallback', reason: 'no_board_date' };
  return day === selectedDay ? { kind: 'stay' } : { kind: 'select_day', day };
}

/**
 * Oct 20 addendum (WS-FIX3, F2): a deep link that asked to OPEN a ticket which IS on the selected Dayboard day but
 * for which the board drew no card — a departed / returned repair (`Blocked`, in no Parking Lot group) or an
 * unassigned continuation day (no column) — used to do nothing at all: `planTicketDeepLink` answers `stay`, no card
 * consumes `openDetails`, so neither the Command Center nor the fallback ever appeared.
 *
 * True when the Dashboard must host that ticket's own Command Center itself. Every input is an observation, never a
 * permission: the ticket row is one the viewer's own RLS read already returned for that day.
 */
export function needsDeepLinkCommandCenterHost(p: {
  /** The `highlight` id the link named. */
  ticketId: string | null;
  /** The link still asks to open details (a card that opened its Command Center consumes the flag). */
  stillWantsDetails: boolean;
  /** The board has the ticket's row for the selected day. */
  rowOnSelectedDay: boolean;
  /** A ticket card for it is mounted on the board. */
  cardMounted: boolean;
}): boolean {
  return !!p.ticketId && p.stillWantsDetails && p.rowOnSelectedDay && !p.cardMounted;
}
